// src/components/sensors/sensorKind.js
// FlowSense sensor tables — which table a sensor belongs to, and how many rows each table shows — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// Why: Live Readings put a sensor in the Pressure table only when `type` was empty or exactly 'pressure',
// and in the Water Level table only when it was exactly 'water_level'. Any other value ('PRESSURE',
// 'water_pressure', 'well', 'float', 'level', …) or a water sensor identified by its well link fell into
// neither table, so the sensor showed (from the cache or from its readings) and then vanished when the
// fresh list arrived. Now every sensor lands in exactly one table, with the same rule the backend uses
// (services/sensorIntelService.isWaterSensor) to pick the readings table and the flood model.
// ═══════════════════════════════════════════════════════════════════════════
import { normalizeId, sensorIdOf } from './sensorLiveStatus';

/** Same list as the backend's isWaterSensor (water-level readings table). */
export const WATER_LEVEL_TYPES = Object.freeze(['water_level', 'well', 'float', 'level']);

/** true → Water Level table; everything else (pressure, no type, unknown types) → Pressure table. */
export const isWaterLevelSensor = (s) => {
  if (!s) return false;
  const type = String(s.type ?? '').trim().toLowerCase();
  if (WATER_LEVEL_TYPES.includes(type)) return true;
  return String(s.linked_entity_type ?? '').trim().toLowerCase() === 'well';
};

export const isPressureSensor = (s) => !!s && !isWaterLevelSensor(s);

/** Splits the register into the two tables in one pass. Every sensor with an id lands in exactly one. */
export const splitSensorsByKind = (sensors) => {
  const pressure = [];
  const waterLevel = [];
  (Array.isArray(sensors) ? sensors : []).forEach((s) => {
    if (!s || sensorIdOf(s) === null) return;
    (isWaterLevelSensor(s) ? waterLevel : pressure).push(s);
  });
  return { pressure, waterLevel };
};

/**
 * How many rows a table shows: its registered sensors ∪ the devices it has readings for
 * (the tables also list devices that send readings but are not in their register), matched the same
 * case-insensitive way the tables match them.
 */
export const countTableRows = (sensors, readings) => {
  const keys = new Set();
  (Array.isArray(sensors) ? sensors : []).forEach((s) => {
    const k = normalizeId(sensorIdOf(s));
    if (k) keys.add(k);
  });
  if (readings && typeof readings === 'object') {
    Object.keys(readings).forEach((id) => {
      if (!readings[id] || typeof readings[id] !== 'object') return;
      const k = normalizeId(id);
      if (k) keys.add(k);
    });
  }
  return keys.size;
};
